<template>
  <div class="container mt-5">
    <!-- Card Details Section -->
    <div v-if="card" class="card mb-4">
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <h1 class="card-title mb-3">{{ card.name }}</h1>
            <div class="card-details">
              <p><strong>Type:</strong> {{ card.type }}</p>
              <p v-if="card.subtype"><strong>Subtype:</strong> {{ card.subtype }}</p>
              <p><strong>Description:</strong> {{ card.description }}</p>
              <p v-if="card.flavorText"><strong>Flavor Text:</strong> {{ card.flavorText }}</p>
              <p v-if="card.parent_id"><strong>Parent Card:</strong> 
                <router-link :to="{ name: 'CardDetail', params: { cardId: card.parent_id }}">
                  View Original Card
                </router-link>
              </p>
            </div>
            <div class="mt-3" v-if="canEdit">
              <button class="btn btn-primary me-2" @click="initNewVariant">
                Create Variant
              </button>
              <button class="btn btn-secondary" @click="editCard">
                Edit Card
              </button>
            </div>
          </div>
          <div class="col-md-6">
            <!-- Card Preview -->
            <div 
              class="card card-preview"
              :class="{ 'dark': card.image_url }"
              :style="{ backgroundImage: card.image_url ? `url('${card.image_url}')` : '' }"
            >
              <div class="primary-header">
                {{ card.name }}
                <i 
                  v-if="card.icon" 
                  :class="['ra', card.icon]" 
                  style="font-size: 20px; margin-left: 10px;"
                ></i>
              </div>
              <div class="primary-block">
                <div class="primary-type-subtype" v-if="card.type || card.subtype">
                  {{ card.type }} {{ card.subtype ? '- ' + card.subtype : '' }}
                </div>
                <div class="primary-block-info">
                  <div class="primary-description">
                    {{ card.description }}
                  </div>
                  <div class="primary-flavor-text" v-if="card.flavorText">
                    <small>{{ card.flavorText }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Variant Form -->
    <div v-if="showVariantForm" class="card mb-4">
      <div class="card-header">
        <h3>{{ isEditingVariant ? 'Edit Variant' : 'Create Variant' }}</h3>
      </div>
      <div class="card-body">
        <form @submit.prevent="createOrUpdateVariant">
          <div class="mb-3">
            <label for="variantName" class="form-label">Card Name</label>
            <input 
              type="text" 
              class="form-control" 
              id="variantName" 
              v-model="variantCard.name"
            >
          </div>
          <div class="mb-3">
            <label for="variantType" class="form-label">Type</label>
            <input 
              type="text" 
              class="form-control" 
              id="variantType" 
              v-model="variantCard.type"
            >
          </div>
          <div class="mb-3">
            <label for="variantSubtype" class="form-label">Subtype</label>
            <input 
              type="text" 
              class="form-control" 
              id="variantSubtype" 
              v-model="variantCard.subtype"
            >
          </div>
          <div class="mb-3">
            <label for="variantDescription" class="form-label">Description</label>
            <textarea 
              class="form-control" 
              id="variantDescription" 
              rows="3"
              v-model="variantCard.description"
            ></textarea>
          </div>
          <div class="mb-3">
            <label for="variantFlavorText" class="form-label">Flavor Text</label>
            <textarea 
              class="form-control" 
              id="variantFlavorText" 
              rows="2"
              v-model="variantCard.flavorText"
            ></textarea>
          </div>
          <div class="mb-3">
            <label for="variantRepeats" class="form-label">Card Repeats</label>
            <input 
              type="number" 
              class="form-control" 
              id="variantRepeats" 
              v-model="variantCard.repeats"
              min="1"
            >
          </div>
          <div class="mb-3">
            <label class="form-label">Card Background Image</label>
            <div class="d-flex gap-2 mb-2">
              <input 
                type="file" 
                ref="cardImageInput"
                @change="uploadCardImage"
                accept="image/*"
                class="d-none"
              >
              <button 
                type="button"
                class="btn btn-outline-primary" 
                @click="$refs.cardImageInput.click()"
              >
                <i class="fas fa-upload me-1"></i> Upload Image
              </button>
            </div>
            <div v-if="variantCard.image_url" class="card-image-preview">
              <img :src="variantCard.image_url" class="img-thumbnail" style="max-height: 100px;" />
              <button 
                type="button"
                class="btn btn-sm btn-danger mt-1" 
                @click="removeCardImage"
              >
                Remove Image
              </button>
            </div>
          </div>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary">
              {{ isEditingVariant ? 'Update Variant' : 'Create Variant' }}
            </button>
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="clearVariantForm"
            >
              {{ isEditingVariant ? 'Clear Variant' : 'Cancel' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Variants Section -->
    <div class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <h3>Card Variants</h3>
          <div class="search-box">
            <input 
              type="text" 
              class="form-control" 
              placeholder="Search variants..."
              v-model="searchQuery"
              @input="filterVariants"
            >
          </div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="filteredVariants.length" class="row g-4">
          <div v-for="variant in filteredVariants" :key="variant.id" class="col-md-4">
            <div class="variant-container">
              <!-- Card Preview -->
              <div 
                class="card card-preview"
                :class="{ 'dark': variant.image_url }"
                :style="{ backgroundImage: variant.image_url ? `url('${variant.image_url}')` : '' }"
              >
                <div class="primary-header">
                  {{ variant.name }}
                  <i 
                    v-if="variant.icon" 
                    :class="['ra', variant.icon]" 
                    style="font-size: 20px; margin-left: 10px;"
                  ></i>
                </div>
                <div class="primary-block">
                  <div class="primary-type-subtype" v-if="variant.type || variant.subtype">
                    {{ variant.type }} {{ variant.subtype ? '- ' + variant.subtype : '' }}
                  </div>
                  <div class="primary-block-info">
                    <div class="primary-description">
                      {{ variant.description }}
                    </div>
                    <div class="primary-flavor-text" v-if="variant.flavorText">
                      <small>{{ variant.flavorText }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="variant-actions mt-2 text-center d-flex gap-2 justify-content-center">
                <router-link 
                  :to="{ name: 'CardDetail', params: { cardId: variant.id }}" 
                  class="btn btn-primary btn-sm"
                >
                  View Details
                </router-link>
                <button 
                  v-if="canEdit"
                  class="btn btn-secondary btn-sm"
                  @click="editVariant(variant)"
                >
                  Edit Variant
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-4">
          <p>No variants found.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { auth, firestore, storage } from '@/firebase'
import { collection, doc, getDoc, getDocs, addDoc, query, where, updateDoc } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'

export default {
  name: 'CardDetailPage',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    const card = ref(null)
    const variants = ref([])
    const showVariantForm = ref(false)
    const searchQuery = ref('')
    const isEditingVariant = ref(false)

    // Initialize variant card with empty data
    const emptyVariantCard = {
      name: '',
      type: '',
      subtype: '',
      description: '',
      flavorText: '',
      repeats: 1,
      parent_id: null,
      image_url: null
    }

    const variantCard = ref({ ...emptyVariantCard })

    const canEdit = computed(() => {
      // Implement your permission logic here
      return true
    })

    const filteredVariants = computed(() => {
      if (!searchQuery.value) return variants.value
      const query = searchQuery.value.toLowerCase()
      return variants.value.filter(variant => 
        variant.name.toLowerCase().includes(query) ||
        variant.description.toLowerCase().includes(query)
      )
    })

    const loadCard = async () => {
      try {
        const cardDoc = doc(firestore, 'cards', route.params.cardId);
        const cardSnap = await getDoc(cardDoc);
        
        if (!cardSnap.exists()) {
          throw new Error('Card not found');
        }
        
        card.value = {
          id: cardSnap.id,
          ...cardSnap.data()
        };
        
        // Initialize empty variant form with parent_id
        clearVariantForm();
        
        // Load variants
        loadVariants();
      } catch (error) {
        console.error('Error loading card:', error);
      }
    };

    const initNewVariant = () => {
      variantCard.value = {
        ...emptyVariantCard,
        name: `${card.value.name} (Variant)`,
        type: card.value.type,
        parent_id: card.value.id
      };
      isEditingVariant.value = false;
      showVariantForm.value = true;
    };

    const loadVariants = async () => {
      try {
        const cardsRef = collection(firestore, 'cards');
        const q = query(cardsRef, where('parent_id', '==', route.params.cardId));
        const querySnapshot = await getDocs(q);
        
        variants.value = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error loading variants:', error);
      }
    };

    const createOrUpdateVariant = async () => {
      try {
        // Add current user's ID to the variant
        const userId = auth.currentUser?.uid;
        if (!userId) {
          throw new Error('User must be logged in to create/update variants');
        }

        const variantData = {
          ...variantCard.value,
          userId,
          updatedAt: new Date()
        };

        const cardsRef = collection(firestore, 'cards');

        if (isEditingVariant.value && variantCard.value.id) {
          // Update existing variant
          const variantRef = doc(firestore, 'cards', variantCard.value.id);
          await updateDoc(variantRef, variantData);

          // Update the variant in the list
          const index = variants.value.findIndex(v => v.id === variantCard.value.id);
          if (index !== -1) {
            variants.value[index] = {
              id: variantCard.value.id,
              ...variantData
            };
          }
        } else {
          // Create new variant
          variantData.createdAt = new Date();
          const docRef = await addDoc(cardsRef, variantData);
          
          // Add the new variant to the list
          const newVariant = {
            id: docRef.id,
            ...variantData
          };
          variants.value.push(newVariant);
        }

        showVariantForm.value = false;
        clearVariantForm();
      } catch (error) {
        console.error('Error creating/updating variant:', error);
        alert('Failed to save variant. Please try again.');
      }
    };

    const editVariant = (variant) => {
      // Load variant data into form
      variantCard.value = { ...variant };
      isEditingVariant.value = true;
      showVariantForm.value = true;
      // Scroll to the form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearVariantForm = () => {
      // Reset form to initial state
      variantCard.value = { 
        ...emptyVariantCard,
        parent_id: card.value?.id 
      };
      isEditingVariant.value = false;
      
      // If we're not editing, also close the form
      if (!showVariantForm.value) {
        showVariantForm.value = false;
      }
    };

    const editCard = () => {
      // Pass the boardGameId as a query parameter
      router.push({
        name: 'EditCard',
        params: { cardId: card.value.id },
        query: { boardGameId: card.value.boardGameId }
      })
    }

    const filterVariants = () => {
      // Filtering is handled by the computed property
    }

    const uploadCardImage = async (event) => {
      // Check if user is logged in
      if (!auth.currentUser) {
        alert('You need to be logged in to upload images');
        return;
      }

      // Get the file
      const file = event.target.files[0];
      
      // Validate file
      if (!file) return;
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File is too large. Maximum size is 5MB.');
        return;
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP).');
        return;
      }

      try {
        // Create a reference to the storage location
        const imageRef = storageRef(
          storage, 
          `card-images/${auth.currentUser.uid}/${Date.now()}_${file.name}`
        );

        // Upload the file
        const snapshot = await uploadBytes(imageRef, file);

        // Get the download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        // Update the variant card with the new image URL
        variantCard.value.image_url = downloadURL;

        // Clear the file input
        event.target.value = null;
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };
    
    const removeCardImage = () => {
      variantCard.value.image_url = null;
    };

    onMounted(() => {
      loadCard()
    })

    return {
      card,
      variants,
      variantCard,
      showVariantForm,
      searchQuery,
      canEdit,
      filteredVariants,
      createOrUpdateVariant,
      editCard,
      filterVariants,
      uploadCardImage,
      removeCardImage,
      editVariant,
      clearVariantForm,
      isEditingVariant,
      initNewVariant
    }
  }
}
</script>

<style scoped>
.card-details p {
  margin-bottom: 0.5rem;
}
.card-preview {
  height: 360px;
  width: 240px;
  border: 1px solid #DDD;
  padding: 12px;
  margin: 2px auto;
  background-size: cover;
  background-position: center;
  position: relative;
  transition: transform 0.2s ease-in-out;
}

.variant-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.variant-container .card-preview:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.variant-actions {
  width: 100%;
  padding: 8px;
}

.search-box {
  width: 300px;
}

/* Card Preview Styles */
.card.dark {
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  background-size: cover;
  background-position: center;
}

.primary-header {
  padding: 10px;
  font-size: 1.2em;
  font-weight: bold;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.primary-block {
  padding: 10px;
}

.primary-type-subtype {
  font-style: italic;
  margin-bottom: 10px;
}

.primary-block-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.primary-description {
  font-size: 1em;
}

.primary-flavor-text {
  font-style: italic;
  opacity: 0.8;
}
</style>